import {
    BymoPageProps,
    doppeHideableValueGetVisibleValue,
    DoppeHtmlSiteInfo,
    DoppeHtmlSitePagesType,
    doppeHtmlSiteResolveAllPostsPageUrl,
    doppeHtmlSiteResolveAllProductsPageUrl,
    doppeHtmlSiteResolveBookOnlinePageUrl,
    doppeHtmlSiteResolveCartPageUrl,
    doppeHtmlSiteResolveEventsPageURL,
    doppeHtmlSiteResolvePricingPlansPageURL
} from '../../../client-server-common';
import {bymoPageGetInnerNavigatablePageById} from '../../../client-server-common/bymo-page-utils/bymo-page-utils';
import {
    autoValueIsAutoValue,
    stringTrimPrefix,
    uiPartCreateMarking,
    validateIsEmail,
    validateIsUrl
} from '@wix/devzai-utils-common';
import {DoppeActionCtaButtonsSettings} from '../../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    DoppeActionTypeWidgetInnerCheckoutButtonParts,
    doppeActionTypeWidgetInnerCheckoutButtonPartsCreateName
} from '../../../components/doppe-action-type-widget-inner-checkout-button/doppe-action-type-widget-inner-checkout-button.ui-parts';
import {
    DoppeActionCTAButton,
    doppeActionCtaButtonLinkResolveTarget,
    doppeActionCtaButtonTypeOpenUrl,
    DoppeActionCTAType,
    doppeActionMultipleCTAButtonIsVisible,
    DoppeActionSettingsCTAButtonSettings,
    DoppeLinkSpecEntityType,
    doppeLinkSpecIsEntity,
    doppeLinkSpecIsPage
} from '../../../client-server-common/types/doppe-action-cta-button';
import {DoppeViewerLang} from '../../../client-server-common/doppe-viewer/doppe-viewer-lang';

export function doppeActionCtaButtonTypeGetLinkSettings(
    ctaButton: DoppeActionCTAButton.BaseType,
    options: {
        sourceURL?: string,
        actionId: string,
        innerItemId?: string,
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
        viewerLang: DoppeViewerLang;
        isLivePreviewMode: boolean;
        isMobileView: boolean;
        /**
         * Optional bymoPageProps for resolving page URLs from page IDs
         */
        bymoPageProps?: BymoPageProps | null;
    }
) {

    const {
        type,
    } = ctaButton.settings;

    const {
        actionId,
        innerItemId,
        sourceURL,
        htmlSiteInfo,
        viewerLang,
        isMobileView,
        isLivePreviewMode,
        bymoPageProps
    } = options;

    const openURL = doppeActionCtaButtonTypeOpenUrl(ctaButton.settings);

    if (openURL) {
        return {
            href: doppeActionCtaButtonTypeResolveURL(ctaButton.settings, {
                sourceURL: sourceURL,
                htmlSiteInfo: htmlSiteInfo,
                viewerLang: viewerLang,
                showMobileView: isMobileView && isLivePreviewMode,
                bymoPageProps: bymoPageProps,
            }),
            target: doppeActionCtaButtonLinkResolveTarget(ctaButton.settings),
            rel: 'noopener',
            tagName: 'a' as const,
        }
    } else if (type === DoppeActionCTAType.Checkout) {
        return {
            ...uiPartCreateMarking(DoppeActionTypeWidgetInnerCheckoutButtonParts.Root, doppeActionTypeWidgetInnerCheckoutButtonPartsCreateName(actionId, innerItemId))
        }
    } else {
        return undefined;
    }
}

export function doppeActionCtaButtonsResolveMainURL(
    ctaButtonsSettings: DoppeActionCtaButtonsSettings,
    options: {
        sourceURL?: string,
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
        viewerLang: DoppeViewerLang;
        isMobileView?: boolean;
        isLivePreviewMode?: boolean;
    }
) {

    const {
        sourceURL,
        htmlSiteInfo,
        viewerLang,
        isMobileView,
        isLivePreviewMode
    } = options;

    for (let i = 0; i < ctaButtonsSettings.ctaButtons.length; i++) {
        const ctaButton = ctaButtonsSettings.ctaButtons[i];
        if (doppeActionMultipleCTAButtonIsVisible(ctaButton)) {
            const url = doppeActionCtaButtonTypeResolveURL(ctaButton.settings, {
                sourceURL: sourceURL,
                htmlSiteInfo: htmlSiteInfo,
                viewerLang: viewerLang,
                showMobileView: !!isMobileView && !!isLivePreviewMode
            });
            if (url) {
                return url;
            }
        }
    }

    return null;
}


export function doppeActionCtaButtonTypeResolveURL(
    ctaSettings: DoppeActionSettingsCTAButtonSettings,
    options: {
        sourceURL?: string,
        htmlSiteInfo: DoppeHtmlSiteInfo | null;
        viewerLang: DoppeViewerLang;
        showMobileView: boolean;
        /**
         * Optional bymoPageProps for resolving page URLs from page IDs
         */
        bymoPageProps?: BymoPageProps | null;
    }
) {

    const {
        sourceURL,
        htmlSiteInfo,
        viewerLang,
        showMobileView,
        bymoPageProps
    } = options;

    switch (ctaSettings.type) {
        case DoppeActionCTAType.Call: {
            return `tel:${ctaSettings.phone}`;
        }
        case DoppeActionCTAType.Email: {

            if (!validateIsEmail(ctaSettings.email)) {
                return undefined;
            }

            return `mailto:${ctaSettings.email}?subject=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customSubject, ''))}&body=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}`
        }
        case DoppeActionCTAType.Share:
        case DoppeActionCTAType.Link: {
            // If url is an AutoValue, use the source URL
            if (autoValueIsAutoValue(ctaSettings.url)) {
                const url = sourceURL ?? '';
                if (validateIsUrl(url, {
                    protocols: ['http', 'https'],
                    require_protocol: true
                })) {
                    return url;
                }
                return '';
            }
            
            // Handle Page link spec - resolve URL from page info
            if (doppeLinkSpecIsPage(ctaSettings.url)) {
                if (bymoPageProps && htmlSiteInfo) {
                    const page = bymoPageGetInnerNavigatablePageById(ctaSettings.url.pageId, bymoPageProps);
                    if (page && page.slug) {
                        // Build URL using base URL + /p/ + slug
                        return `${htmlSiteInfo.htmlSiteUrl}/p/${page.slug}`;
                    }
                }
                // Can't resolve page URL without page info
                return undefined;
            }
            
            // Handle Entity link spec - resolve URL based on entity type
            if (doppeLinkSpecIsEntity(ctaSettings.url)) {
                if (htmlSiteInfo) {
                    const entityType = ctaSettings.url.entityType;
                    const entityId = ctaSettings.url.entityId;
                    
                    // Build entity page URL based on entity type
                    switch (entityType) {
                        case DoppeLinkSpecEntityType.Product:
                            // Products use /product/{id} or similar
                            return `${htmlSiteInfo.htmlSiteUrl}/product/${entityId}`;
                        case DoppeLinkSpecEntityType.BlogPost:
                            // Blog posts use /post/{slug}
                            return `${htmlSiteInfo.htmlSiteUrl}/post/${entityId}`;
                        case DoppeLinkSpecEntityType.Event:
                            // Events use /event/{slug}
                            return `${htmlSiteInfo.htmlSiteUrl}/event/${entityId}`;
                        case DoppeLinkSpecEntityType.Service:
                            // Services use /service/{slug}
                            return `${htmlSiteInfo.htmlSiteUrl}/service/${entityId}`;
                    }
                }
                return undefined;
            }
            
            // url is a string URL
            const url = ctaSettings.url;
            if (validateIsUrl(url, {
                protocols: ['http', 'https'],
                require_protocol: true
            })) {
                return url;
            } else {
                return '';
            }
        }
        case DoppeActionCTAType.IsracardPay: {

            const url = ctaSettings.url;

            if (validateIsUrl(url, {
                protocols: ['http', 'https'],
                require_protocol: true
            })) {
                return url;
            } else {
                return '';
            }
        }
        case DoppeActionCTAType.OpenPage: {
            if (htmlSiteInfo) {
                switch (ctaSettings.pageType) {
                    case DoppeHtmlSitePagesType.Store: {
                        return doppeHtmlSiteResolveAllProductsPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.BookingServices: {
                        return doppeHtmlSiteResolveBookOnlinePageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Cart: {
                        return doppeHtmlSiteResolveCartPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Blog: {
                        return doppeHtmlSiteResolveAllPostsPageUrl(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.PricingPlans: {
                        return doppeHtmlSiteResolvePricingPlansPageURL(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    case DoppeHtmlSitePagesType.Events: {
                        return doppeHtmlSiteResolveEventsPageURL(htmlSiteInfo, viewerLang, showMobileView) ?? undefined;
                    }
                    default: {
                        return undefined;
                    }
                }
            }

            return undefined
        }
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPricingPlan: {
            if (sourceURL && validateIsUrl(sourceURL, {
                protocols: ['http', 'https'],
                require_protocol: true
            })) {
                return sourceURL;
            } else {
                return '';
            }
        }
        case DoppeActionCTAType.Whatsapp: {
            return `https://api.whatsapp.com/send?phone=${stringTrimPrefix(ctaSettings.phone, '+')}&text=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}`;
        }
        case DoppeActionCTAType.Navigate: {
            if (!ctaSettings.address) {
                return undefined;
            }

            return `https://www.google.com/maps/dir/?api=1&destination=${encodeURI(ctaSettings.address)}`
        }
        case DoppeActionCTAType.FacebookMessage: {
            return ctaSettings.userName !== undefined ?
                `https://www.m.me/${ctaSettings.userName}?text=${encodeURI(doppeHideableValueGetVisibleValue(ctaSettings.customMessage, ''))}` :
                undefined;
        }
        case DoppeActionCTAType.InstagramMessage: {
            return ctaSettings.userName !== undefined ?
                `https://ig.me/m/${ctaSettings.userName}` :
                undefined;
        }
        case DoppeActionCTAType.Checkout:
        case DoppeActionCTAType.SaveContact: {
            return undefined;
        }
    }
}


export function doppeCtaButtonIsOpenInnerPageOfVertical(ctaButton: DoppeActionCTAButton.BaseType): boolean {


    switch (ctaButton.settings.type) {
        case DoppeActionCTAType.OpenPricingPlan:
        case DoppeActionCTAType.OpenBlogPost:
        case DoppeActionCTAType.OpenProduct:
        case DoppeActionCTAType.OpenService:
        case DoppeActionCTAType.OpenEvent:
        case DoppeActionCTAType.OpenPage: {
            return true;
        }
        default:
            return false;
    }
}
